// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut, updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context || { currentUser: null, currentUserProfile: null, loading: true };
};

// Create the Provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create or update user profile
  const createUserProfile = async (user, displayName) => {
    console.log('Creating user profile for:', user.uid);
    const userDocRef = doc(db, 'users', user.uid);
    
    // Get fresh user data to ensure we have the latest displayName
    const freshUser = auth.currentUser;
    const finalDisplayName = displayName || freshUser?.displayName || '';
    
    // Extract first and last name from displayName
    const [firstName = '', lastName = ''] = finalDisplayName.split(' ');
    
    const userData = {
      displayName: finalDisplayName,
      firstName,
      lastName,
      email: user.email,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      savedCarts: [],
      role: 'user',
      approvalStatus: 'pending',
      authProvider: user.providerData?.[0]?.providerId === 'google.com' ? 'Google' : (user.providerData?.[0]?.providerId || 'Password')
    };

    try {
      // console.log('Attempting to save user data:', userData);
      await setDoc(userDocRef, userData);
      // console.log('User profile created successfully in Firestore');
      
      // Verify the data was saved
      const savedDoc = await getDoc(userDocRef);
      if (savedDoc.exists()) {
        // console.log('Verified saved profile data:', savedDoc.data());
      } else {
        console.error('Profile creation verification failed - document not found');
      }
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!currentUser) throw new Error('No user logged in');

    const userDocRef = doc(db, 'users', currentUser.uid);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };

    try {
      await updateDoc(userDocRef, updateData);
      // console.log('User profile updated successfully');
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  // Update user auth profile (name, email, password)
  const updateUserAuth = async ({ displayName, email, password }) => {
    if (!currentUser) throw new Error('No user logged in');

    try {
      if (displayName) {
        await updateProfile(currentUser, { displayName });
      }
      if (email) {
        await updateEmail(currentUser, email);
      }
      if (password) {
        await updatePassword(currentUser, password);
      }
      // console.log('User auth profile updated successfully');
    } catch (error) {
      console.error('Error updating user auth profile:', error);
      throw error;
    }
  };

  // Save cart to user profile
  const saveCart = async (cartData, cartName) => {
    if (!currentUser) throw new Error('No user logged in');

    const userDocRef = doc(db, 'users', currentUser.uid);
    const newCart = {
      id: Date.now().toString(),
      name: cartName,
      items: cartData,
      createdAt: new Date().toISOString()
    };

    try {
      await updateDoc(userDocRef, {
        savedCarts: [...(currentUserProfile?.savedCarts || []), newCart],
        updatedAt: new Date().toISOString()
      });
      console.log('Cart saved successfully');
    } catch (error) {
      console.error('Error saving cart:', error);
      throw error;
    }
  };

  // Delete saved cart
  const deleteSavedCart = async (cartId) => {
    if (!currentUser) throw new Error('No user logged in');

    const userDocRef = doc(db, 'users', currentUser.uid);
    const updatedCarts = (currentUserProfile?.savedCarts || []).filter(cart => cart.id !== cartId);

    try {
      await updateDoc(userDocRef, {
        savedCarts: updatedCarts,
        updatedAt: new Date().toISOString()
      });
      console.log('Cart deleted successfully');
    } catch (error) {
      console.error('Error deleting cart:', error);
      throw error;
    }
  };

  useEffect(() => {
    console.log("Auth Provider mounting. Setting up listener.");
    let profileUnsubscribe = null;

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed. User:", user ? { uid: user.uid, email: user.email } : null);
      setCurrentUser(user);

      if (profileUnsubscribe) {
        console.log("Unsubscribing from previous profile listener.");
        profileUnsubscribe();
        profileUnsubscribe = null;
      }
      setCurrentUserProfile(null);

      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        console.log(`User logged in (${user.uid}). Setting up profile listener...`);

        try {
          // First check if profile exists
          const userDoc = await getDoc(userDocRef);
          console.log('Profile check result:', userDoc.exists() ? 'exists' : 'not found');
          
          if (!userDoc.exists()) {
            console.log(`No profile found for user ${user.uid}, creating one...`);
            try {
              // Force a token refresh to ensure we have the latest user data
              await user.getIdToken(true);
              // Add a small delay to ensure token propagation
              await new Promise(resolve => setTimeout(resolve, 500));
              // Get fresh user data
              const freshUser = auth.currentUser;
              await createUserProfile(freshUser, freshUser.displayName);
              console.log('Profile creation completed');
            } catch (error) {
              console.error(`Error creating profile for user ${user.uid}:`, error);
              setLoading(false);
              return;
            }
          }

          profileUnsubscribe = onSnapshot(userDocRef, (docSnap) => {
            console.log('Profile snapshot received:', docSnap.exists() ? 'exists' : 'not found');
            if (docSnap.exists()) {
              const profileData = docSnap.data();
              console.log(`Profile listener update for ${user.uid}:`, profileData);
              setCurrentUserProfile(profileData);
            } else {
              console.warn(`Firestore profile missing for user ${user.uid}!`);
              setCurrentUserProfile(null);
            }
            setLoading(false);
          }, (error) => {
            console.error(`Error listening to profile for user ${user.uid}:`, error);
            setCurrentUserProfile(null);
            setLoading(false);
          });
        } catch (error) {
          console.error('Error in profile setup:', error);
          setLoading(false);
        }
      } else {
        console.log("User logged out.");
        setCurrentUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      console.log("Auth Provider unmounting. Cleaning up listeners.");
      authUnsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, []);

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      console.log("User signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    currentUser,
    currentUserProfile,
    loading,
    signOut,
    createUserProfile,
    updateUserProfile,
    updateUserAuth,
    saveCart,
    deleteSavedCart
  }), [currentUser, currentUserProfile, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 